// FT-448 (was FT-437): Single source of truth for how a crew certificate's expiry is
// normalised on save. A blank/empty expiry = a PERMANENT certificate that never expires,
// stored as { expiry_date: null, doesNotExpire: true }. A dated expiry is kept as-is with
// doesNotExpire: false (no behaviour change for dated certs). The desktop crew-card form
// (CertificateForm.handleSubmit) — which is the SAME responsive form the mobile crew flow
// renders — calls this, so desktop and mobile produce an identical, compliance-valid shape.
// Exported so tests exercise the REAL normalisation the form runs (not a hand-written mirror).
export const normalizeCertificateExpiry = (expiryDate) => {
  const hasExpiry = !!expiryDate;
  return {
    expiry_date: hasExpiry ? expiryDate : null,
    doesNotExpire: !hasExpiry
  };
};

// Certificate requirements based on position and area
// FT-125: Aligned with WBC3 Table A5.1 (Master qualifications per area category)
export const MASTER_CERTIFICATES = {
  0: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean'],
  1: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore'],
  // FT-125 Bug 2: Cat 2 now includes Boatmaster, Yachtmaster Coastal, PB Advanced per WBC3 Table A5.1
  2: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore', 'Boatmaster License', 'RYA Yachtmaster Coastal', 'RYA Advanced Powerboat Commercial'],
  3: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore', 'Boatmaster License', 'RYA Yachtmaster Coastal', 'RYA Advanced Powerboat Commercial'],
  // FT-125 Bug 3: Cat 4 now includes Day Skipper (20nm daylight only) per WBC3 Table A5.1
  4: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore', 'Boatmaster License', 'RYA Yachtmaster Coastal', 'RYA Advanced Powerboat Commercial', 'RYA Day Skipper'],
  // FT-125 Bug 4: Cat 5 does NOT include Day Skipper (night ops allowed, DS is daylight only) per WBC3 Table A5.1
  5: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore', 'Boatmaster License', 'RYA Yachtmaster Coastal', 'RYA Advanced Powerboat Commercial'],
  // FT-125 Bug 6: PB Level 2 only valid for open boats/RIBs <3GT (Note H) - documented for future gating
  6: ['STCW Master (Workboat <500GT)', 'STCW Master <200GT', 'RYA Yachtmaster Ocean', 'RYA Yachtmaster Offshore', 'Boatmaster License', 'RYA Yachtmaster Coastal', 'RYA Advanced Powerboat Commercial', 'RYA Day Skipper', 'RYA Powerboat Level 2 Commercial']
};

export const ENGINEER_CERTIFICATES = {
  'under1500': ['Marine Engine Operator License', 'AEC Part 1'],
  '1500to3000': ['Senior Marine Engine Operator License', 'Small Vessel Second Engineer'],
  'over3000': ['STCW III/2 Small Vessel Chief Engineer']
};

// FT-125 Bug 1: PSSR removed from mandatory - NOT in WBC3 Table A5.3
// Only Sea Survival, Fire Fighting, First Aid are WBC3 mandatory training
export const MANDATORY_TRAINING = [
  'Personal Survival Techniques',
  'Fire Prevention & Fire Fighting',
  'Elementary First Aid'
];

// Parse category string to number (e.g., 'cat2' -> 2, 'Category 3' -> 3)
// Copied from crewCertCheck.js for consistency
// FT-106: Exported for use in crewHelpers.js
export const parseCategory = (category) => {
  if (typeof category === 'number') return category;
  if (!category) return 6; // Default to Cat 6 (most restricted)
  const match = String(category).match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 6;
};

// Parse length value to number (handles strings, undefined, null)
// FT-29: Length can come as string from localStorage or form data
const parseLength = (length) => {
  if (typeof length === 'number' && !isNaN(length)) return length;
  if (!length) return 0;
  const parsed = parseFloat(String(length));
  return isNaN(parsed) ? 0 : parsed;
};

// Get required certificates based on position and vessel
// WBC3 scoping: Some certificates are AT_LEAST_1 (vessel-level) not per-person
export const getRequiredCertificates = (position, vesselAreaCategory, vesselEnginePower, vesselLength, vesselEquipment = {}) => {
  // Parse category to number (handles 'cat4', 'Category 4', 4, etc.)
  const categoryNum = parseCategory(vesselAreaCategory);
  // FT-29: Parse length to number (handles strings from localStorage/form data)
  const lengthM = parseLength(vesselLength);

  // FT-30: Extract equipment flags (with safe defaults)
  // FT-30 Fix: Also check user-selected equipment arrays from Step 6 wizard
  const { hasRadar, hasECDIS, hasCooking, hasGalley, hasStabilityBooklet, operations, navigationEquipment, accommodation } = vesselEquipment || {};
  const navEquipmentArray = Array.isArray(navigationEquipment) ? navigationEquipment : [];

  // Combined equipment detection: check explicit flags OR equipment arrays
  const hasRadarFitted = hasRadar || navEquipmentArray.includes('radar');
  // FT-30 Part 2: Split ECS vs ECDIS — different qualifications per WBC3 Table A5.3
  // Chart plotter → ECS Training (MCA Small Ships Navigation: ECS and Bridge Watchkeeping)
  // Full ECDIS → ECDIS Training (Appropriate ECDIS Training per MIN 698 - equipment in excess of Code requirements)
  const hasChartPlotterFitted = hasECDIS || navEquipmentArray.includes('chart_plotter');
  const hasECDISFitted = navEquipmentArray.includes('ecdis');  // Only explicit ECDIS, not chart plotter
  const hasGalleyEquipped = hasCooking || hasGalley || accommodation?.hasCooking || accommodation?.hasGalley;
  const operationsList = Array.isArray(operations) ? operations : [];

  // Determine scope-aware requirements per WBC3
  // Fire Fighting: <15m = AT_LEAST_1, >=15m = ALL_CREW (Table A5.3 line 17369)
  const fireIsVesselLevel = lengthM > 0 && lengthM < 15;
  // First Aid: Cat 2-6 = AT_LEAST_1 (Table A5.3 line 17365)
  const firstAidIsVesselLevel = categoryNum >= 2;

  // FT-125 Bug 1: PSSR removed - NOT in WBC3 Table A5.3
  // Only Medical and Sea Survival are mandatory for all per WBC3
  const mandatoryForAll = {
    title: 'Mandatory for All Crew',
    certificates: [
      { type: 'medical', label: categoryNum <= 2 ? 'ENG1 Medical Certificate' : 'ML5 Medical Certificate', required: true },
      { type: 'survival', label: 'Personal Survival Techniques (PST)', required: true }
    ]
  };

  // Add fire fighting to mandatoryForAll only for vessels >=15m
  if (!fireIsVesselLevel) {
    mandatoryForAll.certificates.push({ type: 'fire', label: 'Fire Prevention & Fire Fighting', required: true });
  }

  // First Aid / Medical Care requirements vary by category:
  // Cat 0-1: "Proficiency in Medical Care" required for all (higher standard per WBC3 Table A5.3 line 17360)
  // Cat 2-6: "Elementary First Aid" is AT_LEAST_1 (vessel-level)
  if (!firstAidIsVesselLevel) {
    // Cat 0-1: Higher standard medical care required for all
    mandatoryForAll.certificates.push({
      type: 'medical_care',
      label: 'Proficiency in Medical Care',
      required: true,
      note: 'WBC3 Table A5.3: Required for all crew on Cat 0-1 vessels'
    });
  }

  const positionSpecific = {
    title: `Position Specific - ${position || 'Select Position'}`,
    certificates: []
  };

  // Vessel-level requirements (AT_LEAST_1 onboard per WBC3)
  const vesselLevel = {
    title: 'Vessel Requirement - At Least 1 Onboard',
    certificates: []
  };

  // Fire Fighting: <15m vessels only need 1 person (Table A5.3)
  if (fireIsVesselLevel) {
    vesselLevel.certificates.push({
      type: 'fire',
      label: 'Fire Prevention & Fire Fighting',
      required: false,
      note: 'WBC3 Table A5.3: At least 1 crew member required for vessels under 15m'
    });
  }

  // First Aid: Cat 2-6 only need 1 person (Table A5.3)
  if (firstAidIsVesselLevel) {
    vesselLevel.certificates.push({
      type: 'first_aid',
      label: 'Elementary First Aid',
      required: false,
      note: 'WBC3 Table A5.3: At least 1 crew member required for Cat 2-6 vessels'
    });
  }

  // Engineering: Cat 0-2 requires at least 1 qualified engineer (Table A5.2)
  if (categoryNum <= 2) {
    const engCerts = ENGINEER_CERTIFICATES[vesselEnginePower] || ENGINEER_CERTIFICATES['under1500'];
    vesselLevel.certificates.push({
      type: 'eng_cert',
      label: 'Engineering Qualification',
      options: [...engCerts, 'AEC Part 1', 'AEC Part 2'],
      required: false,
      note: 'WBC3 Table A5.2: At least 1 crew member required for Cat 0-2 vessels'
    });
  }

  // Radio Operator: All vessels require at least 1 (Section 28.1.8)
  vesselLevel.certificates.push({
    type: 'radio',
    label: 'Radio Operator Certificate',
    options: ['GMDSS GOC', 'GMDSS ROC', 'VHF DSC Radio Operator'],
    required: false,
    note: 'WBC3 Section 28.1.8: At least 1 qualified radio operator required'
  });

  // ========================================================================
  // FT-30: Equipment-Linked Conditional Cert Requirements
  // These only apply when specific equipment is fitted to the vessel
  // ========================================================================

  // Radar/ARPA Training: Required when vessel has radar fitted (WBC3 Table A5.3 line 17373)
  // Cat 0-2 vessels require radar anyway, so show for them OR if radar explicitly fitted
  if (hasRadarFitted || categoryNum <= 2) {
    vesselLevel.certificates.push({
      type: 'radar_training',
      label: 'Radar/ARPA Training',
      required: false,
      note: hasRadarFitted
        ? 'WBC3 Table A5.3: Required because vessel has radar fitted'
        : 'WBC3 Table A5.3: Required for Cat 0-2 vessels (radar mandatory)'
    });
  }

  // FT-30 Part 2: ECS vs ECDIS — Different qualifications per WBC3 Table A5.3
  // Chart plotter → ECS Training (MCA Small Ships Navigation and Radar: ECS and Bridge Watchkeeping)
  if (hasChartPlotterFitted) {
    vesselLevel.certificates.push({
      type: 'ecs_training',
      label: 'ECS Training',
      required: false,
      note: 'WBC3 Table A5.3: Required for vessels with electronic chart plotter'
    });
  }

  // Full ECDIS → ECDIS Training (Appropriate ECDIS Training per MIN 698)
  // ECDIS is treated as equipment in excess of Code requirements (WBC3 19.3.4)
  if (hasECDISFitted) {
    vesselLevel.certificates.push({
      type: 'ecdis_training',
      label: 'ECDIS Training',
      required: false,
      note: 'WBC3 Table A5.3 Note B: ECDIS is equipment in excess of Code requirements — requires appropriate ECDIS training per MIN 698'
    });
  }

  // Catering Training: Required when vessel has galley with cooking facilities (WBC3 Table A5.3 line 17390)
  if (hasGalleyEquipped) {
    vesselLevel.certificates.push({
      type: 'catering_training',
      label: 'Ship\'s Cook Certificate / Catering Training',
      required: false,
      note: 'WBC3 Table A5.3: Required because vessel has galley with cooking facilities'
    });
  }

  // MGO Transfer Training: Required for ALL crew when vessel conducts fuel transfer operations
  // This is ALL_CREW scope, so goes in mandatoryForAll (WBC3 Section 29.10.2.5 line 14723)
  if (operationsList.includes('fuel_transfer') || operationsList.includes('bunkering')) {
    mandatoryForAll.certificates.push({
      type: 'mgo_transfer',
      label: 'MGO Transfer Training',
      required: true,
      note: 'WBC3 Section 29.10.2.5: Required for all crew on vessels conducting fuel transfer'
    });
  }

  // Handle Skipper same as Master (common workboat position)
  if (position === 'Master' || position === 'Skipper') {
    const masterCerts = MASTER_CERTIFICATES[categoryNum] || MASTER_CERTIFICATES[3];
    positionSpecific.certificates.push({
      type: 'coc',
      label: 'Certificate of Competency',
      options: masterCerts,
      required: true,
      note: `Minimum requirement for Area ${categoryNum} operations`
    });
    positionSpecific.certificates.push({
      type: 'radio',
      label: 'Radio Certificate',
      options: categoryNum === 0 ? ['GMDSS GOC', 'GMDSS ROC'] : ['VHF DSC Radio Operator'],
      required: true
    });

    // FT-30 Part 3: Stability Training required for Master when vessel has SIB
    // WBC3 Table A5.3: Master must complete MCA-approved 1-day stability course
    if (hasStabilityBooklet) {
      positionSpecific.certificates.push({
        type: 'stability_training',
        label: 'Stability Training',
        required: true,
        note: 'WBC3 Table A5.3: Required for Master when vessel has Stability Information Booklet (SIB)'
      });
    }
  }
  
  // Mate requires deck officer qualifications
  if (position === 'Mate') {
    const mateCerts = categoryNum <= 2
      ? ['OOW <500GT', 'Chief Mate <500GT', 'RYA Yachtmaster Offshore', 'RYA Yachtmaster Ocean']
      : ['OOW <500GT', 'Chief Mate <500GT', 'RYA Yachtmaster Coastal', 'RYA Yachtmaster Offshore', 'RYA Day Skipper'];
    positionSpecific.certificates.push({
      type: 'coc',
      label: 'Deck Officer Certificate',
      options: mateCerts,
      required: false,
      note: 'Recommended for Mate position'
    });
  }
  
  if (position === 'Engineer') {
    const engCerts = ENGINEER_CERTIFICATES[vesselEnginePower] || [];
    positionSpecific.certificates.push({
      type: 'eng_cert',
      label: 'Engineering Certificate',
      options: engCerts,
      required: true,
      note: `For ${vesselEnginePower} engine power`
    });
  }

  return { mandatoryForAll, positionSpecific, vesselLevel };
};

// Certificate hierarchy - higher certificates that cover lower ones
const CERTIFICATE_HIERARCHY = {
  // Radio certificates - GMDSS GOC > ROC > VHF DSC
  'VHF DSC Radio Operator': ['GMDSS GOC', 'GMDSS ROC'],
  'GMDSS ROC': ['GMDSS GOC'],
  
  // RYA certificates hierarchy
  'RYA Powerboat Level 2 Commercial': ['RYA Day Skipper', 'RYA Yachtmaster Coastal', 'RYA Yachtmaster Offshore', 'RYA Yachtmaster Ocean', 'RYA Advanced Powerboat Commercial'],
  'RYA Day Skipper': ['RYA Yachtmaster Coastal', 'RYA Yachtmaster Offshore', 'RYA Yachtmaster Ocean'],
  'RYA Yachtmaster Coastal': ['RYA Yachtmaster Offshore', 'RYA Yachtmaster Ocean'],
  'RYA Yachtmaster Offshore': ['RYA Yachtmaster Ocean'],
  
  // Medical certificates - ENG1 covers ML5
  'ML5 Medical Certificate': ['ENG1 Medical Certificate'],

  // Engineering certificates hierarchy
  'Marine Engine Operator License': ['Senior Marine Engine Operator License', 'Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer'],
  'Senior Marine Engine Operator License': ['Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer'],
  'Small Vessel Second Engineer': ['STCW III/2 Small Vessel Chief Engineer'],
  'AEC Part 1': ['AEC Part 2', 'Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer']
};

// Check if a required certificate type is satisfied
export const isCertificateRequirementMet = (requirement, formData) => {
  const certificates = Array.isArray(formData.certificates) ? formData.certificates : [];

  if (requirement.type === 'medical') {
    // FT-28: Check for ENG1/ML5 in the certificates array (not legacy medical_type field)
    // Look for medical certificates with valid expiry dates
    const medicalCerts = certificates.filter(cert => {
      const certType = cert?.type || '';
      return certType.includes('ENG1') || certType.includes('ML5') || certType.includes('Medical Certificate');
    });

    const hasENG1 = medicalCerts.some(cert => (cert?.type || '').includes('ENG1'));
    const hasML5 = medicalCerts.some(cert => (cert?.type || '').includes('ML5'));
    const hasAnyMedical = medicalCerts.length > 0;

    // FT-448 (was FT-437): A permanent (never-expiring) medical cert is valid FOREVER
    // — it must count as meeting the requirement, never flagged missing/overdue.
    //
    // A medical cert can be "permanent" in three shapes, ALL of which are valid-forever:
    //   1. explicit doesNotExpire flag (desktop CertificateForm blank-expiry save);
    //   2. a blank/null expiry with NO flag (older records, and the training-merge in
    //      AddCrewModal which sets expiry_date: cert.expiry || null with no flag — the
    //      same shape a mobile-added permanent cert produces);
    //   3. a dated expiry (behaviour unchanged — the date's own validity is handled on
    //      the display/status side; presence of the requirement is met here).
    //
    // Because `medicalCerts` is the pre-FILTERED set of medical certs the crew member
    // actually holds, its mere presence (`some(cert => !!cert)`) means a held medical
    // cert satisfies the requirement regardless of expiry shape or flag. An ABSENT
    // medical cert never enters this list, so `some` is false on an empty set — a
    // genuinely missing medical cert is NEVER falsely counted as met. This aligns the
    // code with the comment above ("no expiry date at all = valid-forever").
    const hasValidOrPermanent = medicalCerts.some(cert => !!cert);

    if (requirement.label === 'ML5 Medical Certificate') {
      return hasValidOrPermanent && (hasML5 || hasENG1);
    }
    return hasValidOrPermanent && hasAnyMedical;
  }

  // FT-148 FIX: Normalize certificate name for flexible matching
  // Removes punctuation, abbreviations in parentheses, and lowercases for comparison
  const normalizeCertName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/\s*\([^)]*\)\s*/g, '') // Remove parenthetical abbreviations like "(PST)"
      .replace(/[^a-z0-9\s]/g, '')     // Remove punctuation
      .replace(/\s+/g, ' ')            // Normalize spaces
      .trim();
  };

  // Helper function to check if a certificate or its superiors are held
  const hasCertificateOrSuperior = (certName) => {
    const normalizedTarget = normalizeCertName(certName);

    // FT-148 FIX: Use flexible matching - includes() for partial matches
    // This handles cases like "Personal Survival Techniques" matching "Personal Survival Techniques (PST)"
    const hasDirectMatch = certificates.some(cert => {
      if (!cert || !cert.type) return false;
      const normalizedCert = normalizeCertName(cert.type);
      // Check if either contains the other (handles abbreviations and full names)
      return normalizedCert.includes(normalizedTarget) || normalizedTarget.includes(normalizedCert);
    });

    if (hasDirectMatch) return true;

    // Check if they have a superior certificate that covers this one
    const superiorCerts = CERTIFICATE_HIERARCHY[certName] || [];
    return superiorCerts.some(superior => {
      const normalizedSuperior = normalizeCertName(superior);
      return certificates.some(cert => {
        if (!cert || !cert.type) return false;
        const normalizedCert = normalizeCertName(cert.type);
        return normalizedCert.includes(normalizedSuperior) || normalizedSuperior.includes(normalizedCert);
      });
    });
  };
  
  // For requirements with multiple options
  if (requirement.options && Array.isArray(requirement.options)) {
    // Check if any of the options or their superiors are present
    return requirement.options.some(option => hasCertificateOrSuperior(option));
  }
  
  // For specific certificate types
  if (requirement.label) {
    return hasCertificateOrSuperior(requirement.label);
  }
  
  // Fallback to original logic
  return certificates.some(cert => 
    cert && cert.type && (cert.type === requirement.label || cert.type.includes(requirement.label))
  );
};